import {
  Button,
  FormHelperText,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { Alert, AlertTitle, Skeleton } from '@material-ui/lab';
import { ErrorMessage, FieldArray, useFormikContext } from 'formik';
import { inject, observer } from 'mobx-react';
import { withSnackbar } from 'notistack';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AutocompleteWithFormik from '../../../components/inputs/AutocompleteWithFormik';
import InputWithFormik from '../../../components/inputs/InputWithFormik';
import MonedaService from '../../../services/MonedaService';
import UnidadMedidaService from '../../../services/UnidadMedidaService';

import NumberInput from '../../../components/inputs/NumberInput';
import DataTable from '../../../toolkit/DataTable/DataTable';
import ListDataProvider from '../../../toolkit/mobx-connectors/ListDataProvider';
import { mercaderiaDefault } from '../../../utils/data-utils';
import Filter from '../../../utils/filter';
import { formatNumber, toCurrency } from '../../../utils/form-utils';
import createParams from '../../../utils/tables-params';
import MercaderiasAddForm from './MercaderiasAddForm';
import mercaderiasConfig from './MercaderiasConfig';

const useStyles = makeStyles(theme => ({
  container: {
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 0,
      padding: 0
    }
  },
  img: {
    width: '150px',
    [theme.breakpoints.down('sm')]: {
      width: '100px'
    }
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short
    })
  },
  expandOpen: {
    transform: 'rotate(180deg)'
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(4),
    marginLeft: theme.spacing(6),
    marginRight: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: theme.spacing(5),
      padding: theme.spacing(2),
      paddingTop: theme.spacing(4)
    }
  },
  tableCellAction: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  button: {
    margin: 0,
    textDecoration: 'none',
    marginLeft: theme.spacing(1)
  },
  addButon: {
    padding: '15px'
  },
  leftIcon: {
    marginRight: theme.spacing(1)
  },
  rightIcon: {
    marginLeft: theme.spacing(1)
  },
  iconSmall: {
    fontSize: 20
  },

  toolbar: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  cardStyle: {
    borderRadius: '4px 4px 0px 0px'
  },
  tableContainerItem: {
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(3)
  }
}));

const MercaderiasForm = props => {
  const { gridProps } = props;
  const { enqueueSnackbar } = props;
  const classes = useStyles();
  const [mercaderiaConfig, setMercaderiaConfig] = useState({ open: false });
  const [monedas, setMonedas] = useState([]);
  const [unidadMedidas, setUnidadMedidas] = useState([]);
  const [loadingDolares, setLoadingDolares] = useState(false);
  const { t } = useTranslation();
  const formik = useFormikContext();

  const valorTotal = mercaderias => {
    const valor = mercaderias
      ? mercaderias
          .map(m => m.valorVenta)
          .reduce((tot, val) => tot + Number(val), 0)
      : 0;
    formik.setFieldValue('valorTotal', formatNumber(valor));
  };
  const cantidadTotal = mercaderias => {
    const cantidad = mercaderias
      ? mercaderias
          .map(m => m.cantidad)
          .reduce((tot, val) => tot + Number(val), 0)
      : 0;
    formik.setFieldValue('cantidadTotal', cantidad);
  };
  const actualizarPesoTotal = mercaderias => {
    const peso = mercaderias
      ? mercaderias.map(m => m.peso).reduce((tot, val) => tot + Number(val), 0)
      : 0;
    formik.setFieldValue('pesoEstimadoCarga', peso);
  };

  const getTotalDolares = (mercaderias, moneda) => {
    if (mercaderias && mercaderias.length > 0) {
      const valor = mercaderias
        ? mercaderias
            .map(m => m.valorVenta)
            .reduce((tot, val) => tot + Number(val), 0)
        : 0;
      setLoadingDolares(true);
      try {
        MonedaService.getTotalDolares(valor, moneda).then(response => {
          const valorDolares = Object.keys(response.data)[0];
          formik.setFieldValue(
            'totalDolares',
            Number(valorDolares)
              .toFixed(2)
              .toLocaleString()
          );
          formik.setFieldValue('tasas', Object.values(response.data)[0]);
          setLoadingDolares(false);
        });
      } catch (error) {
        enqueueSnackbar(error);
        setLoadingDolares(false);
      }
    } else {
      formik.setFieldValue('totalDolares', '');
      formik.setFieldValue('tasas', '');
    }
  };

  const actualizarTotales = (mercaderias, moneda) => {
    actualizarPesoTotal(mercaderias);
    valorTotal(mercaderias);
    cantidadTotal(mercaderias);
    getTotalDolares(mercaderias, moneda);
  };

  const onEdit = value => {
    setMercaderiaConfig({
      defaultValue: {
        ...value,
        factura: value.factura?.id ? value.factura : ''
      },
      open: true,
      isEdit: true
    });
  };

  const onDelete = value => {
    const newArray = [...formik.values.mercaderias];
    const index = newArray.findIndex(item => item.id === value.id);
    newArray.splice(index, 1);
    formik.setFieldValue(`mercaderias`, newArray);

    actualizarTotales(newArray, formik.values.moneda.codigo);
    props.mercaderia.list.resetListData();
    props.mercaderia.list.dataLoaded(newArray, {
      currentPage: 0,
      pageSize: 10,
      itemsCount: newArray.length
    });
  };

  const configuraciones = mercaderiasConfig({
    classes,
    enqueueSnackbar,
    onEdit,
    onDelete
  });
  const dataset = 'mercaderias';
  const { datosConfig } = configuraciones[dataset];
  const { datosSettings } = configuraciones[dataset];

  const onChangeMercaderia = value => {
    const cantidadMercaderias = formik.values.mercaderias?.length || 0;
    if (value.id) {
      const index = formik.values.mercaderias.findIndex(
        item => item.id === value.id
      );
      formik.setFieldValue(`mercaderias[${index}]`, value);
      props.mercaderia.list.resetListData();
      const newArray = [...formik.values.mercaderias];
      newArray[index] = value;

      actualizarTotales(newArray, formik.values.moneda.codigo);
      props.mercaderia.list.dataLoaded(newArray, {
        currentPage: 0,
        pageSize: 10,
        itemsCount: formik.values.mercaderias.length
      });
    } else {
      const index = cantidadMercaderias + 1;
      const newData = {
        ...value,
        id: index,
        index
      };
      formik.setFieldValue(`mercaderias`, [
        ...(formik.values.mercaderias || []),
        newData
      ]);
      actualizarTotales(
        [...(formik.values.mercaderias || []), newData],
        formik.values.moneda.codigo
      );
      props.mercaderia.list.dataLoaded([newData], {
        currentPage: 0,
        pageSize: 10,
        itemsCount: cantidadMercaderias + 1
      });
    }
  };
  const onReloadTable = () => {
    const settings = datosSettings;
    const params = createParams({ ...props.mercaderia.list, settings });

    if (params.filter) {
      params.filter.and(Filter.path('estado').eq('APROBADO'));
      params.filter = JSON.stringify(params.filter);
    } else {
      params.filter = JSON.stringify(Filter.path('estado').eq('APROBADO'));
    }

    props.mercaderia.list.dataLoaded(formik.values.mercaderias, {
      currentPage: 0,
      pageSize: 10,
      itemsCount: formik.values.mercaderias.length
    });
  };

  useEffect(() => {
    const obtenerMondedas = async () => {
      const response = await MonedaService.getAll();
      if (response.data && response.data.data) {
        setMonedas(response.data.data);
      }
    };
    const obtenerUnidadMedidas = async () => {
      const response = await UnidadMedidaService.getAll();
      if (response.data && response.data.data) {
        setUnidadMedidas(response.data.data);
      }
    };
    obtenerMondedas();
    obtenerUnidadMedidas();
  }, []);

  useEffect(() => {
    if (formik.values.mercaderias && formik.values.moneda.codigo) {
      getTotalDolares(formik.values.mercaderias, formik.values.moneda.codigo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Grid container item>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Formulario para carga de Mercaderías
        </Typography>
      </Grid>

      <Grid container {...gridProps}>
        <Grid item xs={12} md={6} lg={3}>
          <AutocompleteWithFormik
            required
            label="Seleccione la moneda"
            name="moneda"
            options={monedas}
            labelKey="descripcion"
            valueKey="codigo"
            isObjectValue
            afterChange={val =>
              getTotalDolares(formik.values.mercaderias, val.codigo)
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <InputWithFormik
            label="Peso estimado de la carga (kg.)"
            name="pesoEstimadoCarga"
            variant="filled"
            InputProps={{
              disableUnderline: true,
              inputProps: { readOnly: true },
              inputComponent: NumberInput
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <InputWithFormik
            label="Valor total"
            name="valorTotal"
            variant="filled"
            InputProps={{
              disableUnderline: true,
              inputProps: { readOnly: true }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <InputWithFormik
            label="Cantidad total"
            name="cantidadTotal"
            variant="filled"
            InputProps={{
              disableUnderline: true,
              inputProps: { readOnly: true }
            }}
          />
        </Grid>
        {loadingDolares ? (
          <>
            <Grid item xs={12} md={6} lg={3}>
              <Skeleton variant="rect" height={56} />
            </Grid>
            <Grid item xs={12} md={6} lg={9}>
              <Skeleton variant="rect" height={56} />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={6} lg={3}>
              <InputWithFormik
                label="Total en dólares"
                name="totalDolares"
                variant="filled"
                InputProps={{
                  disableUnderline: true,
                  inputProps: { readOnly: true },
                  inputComponent: NumberInput
                }}
              />
            </Grid>

            {formik.values?.tasas?.length > 0 && (
              <Grid item xs={12} md={6} lg={9}>
                <Alert severity="info">
                  <AlertTitle>Tasas utilizadas</AlertTitle>
                  {!formik.values?.tasas && (
                    <span>No se pudo obtener información</span>
                  )}
                  {formik.values?.tasas?.map(tasa => (
                    <Fragment key={tasa.descripcionMoneda}>
                      <span>
                        {tasa.descripcionMoneda} - compra:{' '}
                        {toCurrency(`${tasa.compra}`)} Gs., venta:{' '}
                        {toCurrency(`${tasa.venta}`)} Gs.
                      </span>
                      <br />
                    </Fragment>
                  ))}
                </Alert>
              </Grid>
            )}
          </>
        )}
        <MercaderiasAddForm
          onChange={onChangeMercaderia}
          config={mercaderiaConfig}
          onClose={() =>
            setMercaderiaConfig({ ...mercaderiaConfig, open: false })
          }
          gridProps={gridProps}
          facturas={formik.values.facturas}
          moneda={formik.values.moneda}
          unidadMedidas={unidadMedidas}
        />
        <Grid container item spacing={1} className={classes.tableContainerItem}>
          <Grid item>
            <ListDataProvider
              listStore={props.mercaderia.list}
              render={listProps => {
                const newListProps = listProps;
                if (newListProps.dataTableState) {
                  delete newListProps.dataTableState.pagerData;
                }

                return (
                  <DataTable
                    title={datosConfig.titulo}
                    disableReload
                    settings={datosSettings}
                    onReloadTable={() => onReloadTable()}
                    fileName={datosConfig.servicio}
                    error={
                      formik.errors.mercaderias && formik.touched.mercaderias
                    }
                    toolbarActions={[
                      <Button
                        key="agregar"
                        variant="outlined"
                        color="primary"
                        className={classes.button}
                        startIcon={<Add />}
                        onClick={() =>
                          setMercaderiaConfig({
                            defaultValue: mercaderiaDefault,
                            open: true
                          })
                        }
                      >
                        Agregar
                      </Button>
                    ]}
                    {...listProps}
                  />
                );
              }}
            />
            <ErrorMessage
              name="mercaderias"
              render={msg => {
                if (!Array.isArray(msg)) {
                  return <FormHelperText error>{t(msg)}</FormHelperText>;
                }
                return null;
              }}
            />
            <FieldArray name="mercaderias">
              {() => (
                <>
                  {formik.values.mercaderias.length > 0 &&
                    formik.values.mercaderias.map((mercaderia, index) => (
                      <div key={index}>
                        <>
                          <ErrorMessage
                            name={`mercaderias.${index}.factura.id`}
                            render={msg => (
                              <FormHelperText error>{`${t(
                                msg
                              )} para la mercadería "${
                                mercaderia.descripcion
                              }"`}</FormHelperText>
                            )}
                          />
                        </>
                      </div>
                    ))}
                </>
              )}
            </FieldArray>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default inject('mercaderia')(observer(withSnackbar(MercaderiasForm)));
